import { createApiCall } from "services/api/createApiCall";
import { transformErrorResponse } from "services/api/transformation";

import {
  PricingOverride,
  PricingOverrideEditFormValues,
  PricingOverridePreview,
  PricingOverrideReport,
} from "./overrideTypes";
import { PricingAdjustmentType } from "./schemeTypes";

const BASE = "/v1/admin/pricing-overrides";

/** Backend očekuje broj: 0 = Percent, 1 = Absolute. */
const toAdjustmentNumber = (type: PricingAdjustmentType): number =>
  type === "Absolute" ? 1 : 0;

const transformOverride = (res: any): PricingOverride => ({
  id: res.id,
  tenantId: res.tenantId,
  keyCode: res.keyCode,
  adjustmentType: res.adjustmentType,
  value: res.value,
  reason: res.reason,
  validFrom: res.validFrom,
  validTo: res.validTo,
  status: res.status,
  state: res.state,
  expiresInDays: res.expiresInDays,
  approvedByUserId: res.approvedByUserId ?? null,
  approvedAt: res.approvedAt ?? null,
  rejectionReason: res.rejectionReason ?? null,
  userCreatedId: res.userCreatedId ?? null,
  created: res.created,
  monthlyEffect: res.monthlyEffect ?? null,
});

/*---------------------------------------------------------------------------------------
    SVA PREGAZIVANJA — podloga za izvještaj o odstupanjima
-----------------------------------------------------------------------------------------*/
export const getAllPricingOverridesAPI = async (): Promise<PricingOverrideReport> => {
  const response = await createApiCall(
    { path: BASE, method: "GET" },
    { getMessage: { error: (error: any) => `${transformErrorResponse(error)}` } }
  )();

  return {
    overrides: (response.data?.results ?? []).map(transformOverride),
    measuredMonthlyEffect: response.data?.measuredMonthlyEffect ?? 0,
    unmeasuredKeys: response.data?.unmeasuredKeys ?? [],
  };
};

/*---------------------------------------------------------------------------------------
    PREGAZIVANJA JEDNOG KLIJENTA
-----------------------------------------------------------------------------------------*/
export const getTenantPricingOverridesAPI = async (
  tenantId: string
): Promise<PricingOverride[]> => {
  const response = await createApiCall(
    { path: `/v1/admin/tenants/${tenantId}/pricing-overrides`, method: "GET" },
    { getMessage: { error: (error: any) => `${transformErrorResponse(error)}` } }
  )();

  return (response.data?.results ?? []).map(transformOverride);
};

/*---------------------------------------------------------------------------------------
    PRETPREGLED

    Zove se dok predstavnik tipka — nitko ne smije sam množiti shemu i pregazivanje.
-----------------------------------------------------------------------------------------*/
export const previewPricingOverrideAPI = async (
  tenantId: string,
  keyCode: string,
  adjustmentType: PricingAdjustmentType,
  value: number,
  onDate?: string,
  /** Za čarobnjak: shema je ondje tek odabrana, a još nije dodijeljena klijentu. */
  pricingSchemeId?: string | null
): Promise<PricingOverridePreview> => {
  const params = new URLSearchParams({
    tenantId,
    keyCode,
    adjustmentType: String(toAdjustmentNumber(adjustmentType)),
    value: String(value),
  });

  if (onDate) {
    params.set("onDate", onDate);
  }

  if (pricingSchemeId) {
    params.set("pricingSchemeId", pricingSchemeId);
  }

  const response = await createApiCall(
    { path: `${BASE}/preview?${params.toString()}`, method: "GET" },
    { getMessage: { error: (error: any) => `${transformErrorResponse(error)}` } }
  )();

  return {
    keyCode: response.data.keyCode,
    standard: response.data.standard,
    afterScheme: response.data.afterScheme,
    effective: response.data.effective,
    discountPercent: response.data.discountPercent,
    cost: response.data.cost ?? null,
    margin: response.data.margin ?? null,
    outcome: response.data.outcome,
  };
};

/*---------------------------------------------------------------------------------------
    SPREMANJE
-----------------------------------------------------------------------------------------*/
export const savePricingOverrideAPI = async (
  formValues: PricingOverrideEditFormValues
): Promise<PricingOverride> => {
  const response = await createApiCall(
    {
      path: `/v1/admin/tenants/${formValues.tenantId}/pricing-overrides`,
      method: "POST",
      body: {
        id: formValues.id,
        keyCode: formValues.keyCode,
        adjustmentType: toAdjustmentNumber(formValues.adjustmentType),
        value: Number(formValues.value ?? 0),
        reason: formValues.reason,
        validFrom: formValues.validFrom,
        validTo: formValues.validTo,
      },
    },
    {
      getMessage: {
        success: () => "Poseban uvjet je spremljen",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformOverride(response.data);
};

/*---------------------------------------------------------------------------------------
    ODOBRENJE I ODBIJANJE
-----------------------------------------------------------------------------------------*/
export const approvePricingOverrideAPI = async (id: string): Promise<PricingOverride> => {
  const response = await createApiCall(
    { path: `${BASE}/${id}/approve`, method: "POST" },
    {
      getMessage: {
        success: () => "Poseban uvjet je odobren",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformOverride(response.data);
};

export const rejectPricingOverrideAPI = async (
  id: string,
  reason: string
): Promise<PricingOverride> => {
  const response = await createApiCall(
    { path: `${BASE}/${id}/reject`, method: "POST", body: { reason } },
    {
      getMessage: {
        success: () => "Poseban uvjet je odbijen",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformOverride(response.data);
};

export const deletePricingOverrideAPI = async (id: string): Promise<void> => {
  await createApiCall(
    { path: `${BASE}/${id}`, method: "DELETE" },
    {
      getMessage: {
        success: () => "Poseban uvjet je obrisan",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();
};
